import React, { useState, useEffect } from "react";
import { ArrowLeft, Eye, Calendar, Tag, MessageSquare, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MainHeader from "../MainHeader";
import { useAuth } from "../hooks/useAuth";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
export default function BizHistory() {
  const { api } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [selectedSessionMeta, setSelectedSessionMeta] = useState(null);
  const [selectedSessionData, setSelectedSessionData] = useState(null);
  const navigate = useNavigate();

  // 커스텀 스크롤바 스타일
  const scrollbarStyles = `
    <style>
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #14b8a6;
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #0d9488;
      }
    </style>
  `;

  // 세션 리스트 불러오기
  useEffect(() => {
    api
      .get("simulator/list")
      .then((res) => {
        console.log(res);
        if (res.data && res.data.data) {
          setSessions(res.data.data);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch sessions:", err);
      });
  }, [api]);

  const getStatusColor = (status) => {
    switch (status) {
      case "in_progress":
        return "bg-green-100 text-green-700";
      case "completed":
        return "bg-blue-100 text-blue-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "failed":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "in_progress":
        return "진행중";
      case "completed":
        return "완료";
      case "pending":
        return "대기중";
      case "failed":
        return "실패";
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/\. /g, ".")
      .replace(/\.$/, "");
  };

  const openChatModal = (sessionMeta) => {
    api
      .get("simulator/messages", { params: { session_id: sessionMeta.id } })
      .then((res) => {
        const data = res.data || {};
        setSelectedSessionMeta(sessionMeta);
        setSelectedSessionData({
          messages: data.messages || [],
          result: data.result || null,
        });
      })
      .catch((err) => {
        console.error("Failed to fetch session messages:", err);
      });
  };

  const closeChatModal = () => {
    setSelectedSessionMeta(null);
    setSelectedSessionData(null);
  };

  const ChatModal = ({ meta, data, onClose }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">{meta.business_name}</h2>
                <p className="text-teal-100 mt-1">비즈니스 대화 기록</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Chat History */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6 custom-scrollbar">
            <div className="space-y-4">
              {data.messages.map((msg, index) => (
                <div
                  key={msg.id || index}
                  className="border-l-4 border-teal-400 pl-4 py-2"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-teal-600">
                      {msg.role}
                    </span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-400">
                      라운드 {msg.round}
                    </span>
                    {msg.sentiment !== undefined && (
                      <>
                        <span className="text-xs text-gray-400">•</span>
                        <span
                          className={`text-xs ${
                            msg.sentiment > 0
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          감정: {(msg.sentiment * 100).toFixed(0)}%
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-gray-700 leading-relaxed">{msg.content}</p>
                </div>
              ))}
            </div>

            {/* Final Result */}
            {data.result && (
              <div className="mt-8 p-6 bg-teal-50 rounded-xl">
                <h3 className="text-lg font-bold text-teal-800 mb-4">
                  최종 결과
                </h3>
                <p className="text-gray-700 mb-4">
                  {" "}
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {data.result.final_plan}
                  </ReactMarkdown>
                </p>

                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <p className="text-gray-600">합의도</p>
                    <p className="text-xl font-bold text-teal-600">
                      {(data.result.consensus_level * 100).toFixed(0)}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-600">리스크</p>
                    <p className="text-xl font-bold text-orange-600">
                      {(data.result.risk_score * 100).toFixed(0)}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-600">실행가능성</p>
                    <p className="text-xl font-bold text-green-600">
                      {(data.result.viability_score * 100).toFixed(0)}%
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />

        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-cyan-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="group flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 text-white hover:bg-white/30 transition-all duration-300"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:transform group-hover:-translate-x-1 transition-transform" />
                  <span className="text-sm font-medium">뒤로가기</span>
                </button>
                <h1 className="text-2xl font-bold text-white">
                  비즈니스 히스토리
                </h1>
              </div>
              <MessageSquare className="w-8 h-8 text-white/80" />
            </div>
          </div>
        </div>

        {/* Business List */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid gap-6">
            {sessions.map((session) => (
              <div
                key={session.id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-xl font-bold text-gray-900">
                          {session.business_name}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                            session.status
                          )}`}
                        >
                          {getStatusText(session.status)}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(session.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Tag className="w-4 h-4" />
                          <span>
                            라운드 {session.current_round}/
                            {session.total_rounds}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          <span>{session.message_count || 0}개의 대화</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => openChatModal(session)}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg hover:shadow-lg transition-all duration-300"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="font-medium">산출물 보기</span>
                    </button>
                  </div>

                  {/* Preview of chat */}
                  {session.latest_message && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-600">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-teal-600">
                            {session.latest_message.role}
                          </span>
                          <span className="text-xs text-gray-400">
                            최근 대화
                          </span>
                        </div>
                        <p className="text-gray-700 line-clamp-2">
                          {session.latest_message.content}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Chat Modal */}
        {selectedSessionMeta && selectedSessionData && (
          <ChatModal
            meta={selectedSessionMeta}
            data={selectedSessionData}
            onClose={closeChatModal}
          />
        )}
      </div>
    </>
  );
}
